import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import thebprLogo from "../assets/logo/app_logo/thebpr_ai.svg";
import ArticleOutlinedIcon               from "@mui/icons-material/ArticleOutlined";
import PeopleAltOutlinedIcon             from "@mui/icons-material/PeopleAltOutlined";
import AssignmentTurnedInOutlinedIcon    from "@mui/icons-material/AssignmentTurnedInOutlined";

// ─── Public types ─────────────────────────────────────────────────────────────
export type WelcomeNavTarget = "forms" | "people" | "submissions" | "files";

export interface WelcomeCardDef {
  key: WelcomeNavTarget;
  icon?: React.ReactNode;
  title: string;
  subtitle: string;
}

// ─── Per-agent card configs (export so Dashboard can import them) ─────────────
export const SKILLS_CARDS: WelcomeCardDef[] = [
  {
    key: "forms",
    icon: <ArticleOutlinedIcon sx={{ fontSize: "2.2rem", color: "primary.main" }} />,
    title: "Forms",
    subtitle: "Start by uploading your first form",
  },
  {
    key: "people",
    icon: <PeopleAltOutlinedIcon sx={{ fontSize: "2.2rem", color: "primary.main" }} />,
    title: "People",
    subtitle: "Start by adding your people",
  },
  {
    key: "submissions",
    icon: <AssignmentTurnedInOutlinedIcon sx={{ fontSize: "2.2rem", color: "primary.main" }} />,
    title: "View Submissions",
    subtitle: "Start by viewing your acknowledgements",
  },
];

export const SIGNAL_CARDS: WelcomeCardDef[] = [
  {
    key: "files",
    icon: <ArticleOutlinedIcon sx={{ fontSize: "2.2rem", color: "primary.main" }} />,
    title: "BPR Files",
    subtitle: "Start by announcing new content!",
  },
];

// ─── Props ────────────────────────────────────────────────────────────────────
interface WelcomeViewProps {
  cards: WelcomeCardDef[];
  onNavigate?: (target: WelcomeNavTarget) => void;
}

// ─── Card item ────────────────────────────────────────────────────────────────
const CardItem: React.FC<{
  card: WelcomeCardDef;
  onNavigate?: (target: WelcomeNavTarget) => void;
}> = ({ card, onNavigate }) => (
  <Box
    onClick={() => onNavigate?.(card.key)}
    sx={{
      flex: "1 1 340px",
      minHeight: 160,
      bgcolor: "background.paper",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: "14px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.5,
      px: 4,
      py: 4,
      cursor: onNavigate ? "pointer" : "default",
      transition: "border-color 0.2s, filter 0.2s",
      "&:hover": onNavigate ? { borderColor: "primary.main", filter: "brightness(1.15)" } : {},
    }}
  >
    {card.icon}
    <Typography
      variant="body1"
      sx={{ fontWeight: 700,  color: "text.primary", fontSize: "1.05rem", letterSpacing: "0.06em" }}
    >
      {card.title}
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: "text.secondary", textAlign: "center", fontSize: "0.85rem" }}
    >
      {card.subtitle}
    </Typography>
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────
const WelcomeView: React.FC<WelcomeViewProps> = ({ cards, onNavigate }) => {
  const theme = useTheme();

  // Layout: first two cards side-by-side in row 1, remainder centred below
  const row1 = cards.slice(0, 2);
  const row2 = cards.slice(2);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        px: 4,
        pb: 4,
        overflow: "auto",
        bgcolor: "background.default",
      }}
    >
      {/* Logo + tagline */}
      <Box sx={{ textAlign: "center", mb: 7 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography
            sx={{
              fontSize: { xs: "2rem", sm: "2.8rem", md: "3.4rem" },
              fontWeight: 900,
              color: "text.primary",
              lineHeight: 1.15,
              letterSpacing: "0.02em",
              fontFamily: "'Public Sans', 'Montserrat', arial, sans-serif",
            }}
          >
            Welcome to
          </Typography>
          <Box
            component="img"
            src={thebprLogo}
            alt="TheBPR.ai"
            sx={{ height: { xs: "2rem", sm: "2.8rem", md: "3.4rem" }, width: "auto", display: "block" }}
          />
        </Box>

        <Typography
          variant="body1"
          sx={{ color: "text.secondary", mt: 1.5, fontSize: "1rem", letterSpacing: "0.05em" }}
        >
          Scale your People, Products &amp; Processes
        </Typography>
      </Box>

      {/* How to get started */}
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, mb: 4, color: "text.primary", letterSpacing: "0.05em" }}
      >
        How to get{" "}
        <Box
          component="span"
          sx={{
            background: theme.custom_gradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          started?
        </Box>
      </Typography>

      {/* Row 1 */}
      {row1.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            justifyContent: "center",
            mb: row2.length > 0 ? 3 : 0,
            width: "100%",
            maxWidth: 860,
          }}
        >
          {row1.map(card => (
            <CardItem key={card.key} card={card} onNavigate={onNavigate} />
          ))}
        </Box>
      )}

      {/* Row 2 (remaining cards, each capped at 420px wide and centred) */}
      {row2.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            justifyContent: "center",
            width: "100%",
            maxWidth: row2.length === 1 ? 420 : 860,
          }}
        >
          {row2.map(card => (
            <CardItem key={card.key} card={card} onNavigate={onNavigate} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default WelcomeView;